import type {
  ApiRequestBody,
  ApiResponseBody,
} from "@/lib/api/api-type-helper";
import { apiClient } from "@/lib/api/client";
import { getDataOrThrowApiError } from "@/lib/api/error";

import type {
  GetGovProfileDetailResponse,
  SaveApplicantRequest,
  SaveContactRequest,
} from "./type";

// ============================================
// Profile
// ============================================

/**
 * プロファイル詳細取得
 */
export async function getGovProfileDetail(
  profileId: string,
): Promise<GetGovProfileDetailResponse> {
  const res = await apiClient.GET("/v2/gov-profile/{profileId}/detail", {
    params: { path: { profileId } },
  });
  return getDataOrThrowApiError(res);
}

/**
 * 申請者情報更新
 */
export async function saveApplicant(
  profileId: string,
  data: SaveApplicantRequest,
): Promise<void> {
  const res = await apiClient.PUT("/v2/gov-profile/{profileId}/applicant", {
    params: { path: { profileId } },
    body: data,
  });
  getDataOrThrowApiError(res);
}

/**
 * 連絡先情報更新
 */
export async function saveContact(
  profileId: string,
  data: SaveContactRequest,
): Promise<void> {
  const res = await apiClient.PUT("/v2/gov-profile/{profileId}/contact", {
    params: { path: { profileId } },
    body: data,
  });
  getDataOrThrowApiError(res);
}

// ============================================
// Certificate
// ============================================

export type CertificateStatusResponse = ApiResponseBody<
  "/v2/gov-profile/{profileId}/certificate/status",
  "get"
>;

export type UploadCertificateParams = {
  file: File;
  pin: string;
};

export type UploadCertificateResponse = ApiResponseBody<
  "/v2/gov-profile/{profileId}/certificate",
  "post"
>;

export async function getCertificateStatus(
  profileId: string,
): Promise<CertificateStatusResponse> {
  const res = await apiClient.GET(
    "/v2/gov-profile/{profileId}/certificate/status",
    {
      params: { path: { profileId } },
    },
  );
  return getDataOrThrowApiError(res);
}

export async function uploadCertificate(
  profileId: string,
  params: UploadCertificateParams,
): Promise<UploadCertificateResponse> {
  const formData = new FormData();
  formData.append(
    "certificate",
    params.file,
    encodeURIComponent(params.file.name),
  );
  formData.append("pin", params.pin);

  const res = await apiClient.POST("/v2/gov-profile/{profileId}/certificate", {
    params: { path: { profileId } },
    // @ts-expect-error - スキーマはJSONを期待するが、実際のAPIはmultipart/form-dataを受け付ける
    body: formData,
    bodySerializer: () => formData,
  });
  return getDataOrThrowApiError(res);
}

// ============================================
// OAuth
// ============================================

export type OAuthCallbackRequest = ApiRequestBody<
  "/v2/gov-profile/{profileId}/oauth/callback",
  "post"
>;

export type OAuthCallbackResponse = ApiResponseBody<
  "/v2/gov-profile/{profileId}/oauth/callback",
  "post"
>;

export type OAuthStatusResponse = ApiResponseBody<
  "/v2/gov-profile/{profileId}/oauth/status",
  "get"
>;

export type InitiateOAuthResponse = ApiResponseBody<
  "/v2/gov-profile/{profileId}/oauth/authorize",
  "post"
>;

export async function handleOAuthCallback(
  profileId: string,
  data: OAuthCallbackRequest,
): Promise<OAuthCallbackResponse> {
  const res = await apiClient.POST(
    "/v2/gov-profile/{profileId}/oauth/callback",
    {
      params: { path: { profileId } },
      body: data,
    },
  );
  return getDataOrThrowApiError(res);
}

export async function getOAuthStatus(
  profileId: string,
): Promise<OAuthStatusResponse> {
  const res = await apiClient.GET("/v2/gov-profile/{profileId}/oauth/status", {
    params: { path: { profileId } },
  });
  return getDataOrThrowApiError(res);
}

export async function initiateOAuth(
  profileId: string,
): Promise<InitiateOAuthResponse> {
  const res = await apiClient.POST(
    "/v2/gov-profile/{profileId}/oauth/authorize",
    {
      params: { path: { profileId } },
    },
  );
  return getDataOrThrowApiError(res);
}
